import React, { type FunctionComponent, useMemo, useCallback } from 'react'
import moment, { type Moment } from 'moment'
import uniq from 'lodash/uniq'
import flatMap from 'lodash/flatMap'
import { useBooleanState } from '@retailer-platform/shared-components'
import {
  useStartDate,
  useIsWeek,
  useSelectedStoreGroups,
  useSelectedFilters,
} from 'domains/aggregate-store-performance/utils/hooks/AggregateStorePerformanceQueryParams.hook'
import { useRetailerId } from 'utils/routing/params.hooks'
import { AggregateStorePerformanceOverviewHeader } from 'domains/aggregate-store-performance/components/header/AggregateStorePerformanceOverviewHeader'
import { FilterBar } from 'domains/aggregate-store-performance/components/filter-bar/FilterBar'
import { PickingPerformanceOverview } from 'gin-and-tonic/containers/picking-performance-overview'
import { StoreMetricsTable } from 'domains/aggregate-store-performance/components/store-metrics-table/StoreMetricsTable'
import { useGetStoreGroups } from 'domains/aggregate-store-performance/components/store-group-dropdown/utils/StoreGroupDropdown.hooks'
import { useDomainMessages } from 'domains/aggregate-store-performance/utils/domain/intl'
import { MAX_WEEKS_IN_FUTURE_EDITABLE } from 'domains/aggregate-store-performance/utils/constants/AggregateStorePerformanceOverviewPage.constants'
import {
  getFutureCutoff,
  toStartOfWeek,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.helpers'
import {
  useTrackPageLoad,
  useTrackDateSelection,
  trackPickupAnalyticsClicked,
} from 'domains/aggregate-store-performance/utils/helpers/AggregateStorePerformanceOverviewPage.tracking'
import { useGetFilterOptions } from 'domains/aggregate-store-performance/components/filters-dropdown/utils/FiltersDropdown.hooks'
import { HistoricalStorePerformanceMetrics } from 'domains/aggregate-store-performance/components/historical-store-performance-metrics/HistoricalStorePerformanceMetrics'
import { useGetPickupStoreHistoricalPerformanceMetrics } from 'domains/aggregate-store-performance/utils/hooks/GetAggregateHistoricalStorePerformanceMetrics.hook'

interface StorePerformanceOverviewContentProps {}

export const StorePerformanceOverviewContent: FunctionComponent<
  StorePerformanceOverviewContentProps
> = () => {
  useTrackPageLoad()

  const messages = useDomainMessages({
    pickingPerformanceOverviewSubtitle:
      'aggregateStorePerformanceDomain.pickingPerformanceOverview.subtitle',
    pickingPerformanceOverviewTitle:
      'aggregateStorePerformanceDomain.pickingPerformanceOverview.title',
  })

  const now = useMemo(() => moment().startOf('day'), [])

  const [isFiltersMenuOpen, showFiltersMenu, hideFiltersMenu] = useBooleanState()
  const { isWeek, setIsWeek } = useIsWeek()
  const { startDate, setStartDate } = useStartDate(now)
  const {
    storeGroups,
    loading: storeGroupsLoading,
    refetch: refetchStoreGroups,
  } = useGetStoreGroups()

  const { selectedStoreGroups, setSelectedStoreGroups } = useSelectedStoreGroups(
    storeGroups,
    storeGroupsLoading
  )

  const { filters: groupedFilters } = useGetFilterOptions()
  const filters = useMemo(() => flatMap(groupedFilters, group => group.options), [groupedFilters])
  const { selectedFilters, setSelectedFilters } = useSelectedFilters(filters)

  useTrackDateSelection({ isWeek, startDate })

  const selectedRetailerLocationIds: string[] = useMemo(
    () =>
      uniq(
        selectedStoreGroups.reduce(
          (acc, storeGroup) => [...acc, ...storeGroup.retailerLocationIds],
          []
        )
      ),
    [selectedStoreGroups]
  )

  const {
    updatedAt,
    perStoreMetrics,
    aggregateStoreMetrics,
    loading: metricsLoading,
  } = useGetPickupStoreHistoricalPerformanceMetrics({
    filters: selectedFilters,
    warehouseLocationIds: selectedRetailerLocationIds,
    startDate,
    isWeek,
  })

  const onToggleDayWeek = useCallback(
    (isWeek: boolean) => {
      setIsWeek(isWeek)
      if (!toStartOfWeek(startDate).isSame(startDate)) {
        if (toStartOfWeek(startDate).isAfter(getFutureCutoff(now))) {
          setStartDate(toStartOfWeek(startDate).subtract(1, 'week'))
        } else {
          setStartDate(toStartOfWeek(startDate))
        }
      }
    },
    [setIsWeek, setStartDate, startDate, now]
  )

  const handleDateChange = useCallback(
    (date: Moment | string) => {
      const newDate = moment(date)
      setStartDate(newDate)
    },
    [setStartDate]
  )

  const pickingPerformanceOverviewSubtitle = useMemo(
    () => `${perStoreMetrics.length} ${messages.pickingPerformanceOverviewSubtitle}`,
    [perStoreMetrics, messages]
  )

  const shouldShowEditButtonInTable =
    startDate.isSameOrAfter(now) && startDate.week() - now.week() <= MAX_WEEKS_IN_FUTURE_EDITABLE

  const retailerId = useRetailerId()
  const handlePickupAnalyticsClicked = useCallback(() => {
    trackPickupAnalyticsClicked({ retailerId })
  }, [retailerId])

  return (
    <>
      <AggregateStorePerformanceOverviewHeader />
      <FilterBar
        isWeek={isWeek}
        onToggleDayWeek={onToggleDayWeek}
        storeGroups={storeGroups}
        selectedStoreGroups={selectedStoreGroups}
        isStoreGroupsLoading={storeGroupsLoading}
        setSelectedStoreGroups={setSelectedStoreGroups}
        refetchStoreGroups={refetchStoreGroups}
        startDate={startDate}
        handleDateChange={handleDateChange}
        perStoreMetrics={perStoreMetrics}
        updatedAt={updatedAt}
        now={now}
        filters={groupedFilters}
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
        isFiltersMenuOpen={isFiltersMenuOpen}
        onFilterMenuClose={hideFiltersMenu}
      />
      <PickingPerformanceOverview
        title={messages.pickingPerformanceOverviewTitle}
        subtitle={pickingPerformanceOverviewSubtitle}
        isLoading={metricsLoading}
        showAnalyticsCta
        onAnalyticsCtaClicked={handlePickupAnalyticsClicked}
      >
        <HistoricalStorePerformanceMetrics
          {...aggregateStoreMetrics}
          isFirstDayOfCurrentWeek={now.isSame(toStartOfWeek(now), 'day')}
          hasStoresSelected={Boolean(perStoreMetrics.length)}
        />
      </PickingPerformanceOverview>
      <StoreMetricsTable
        showEditButton={shouldShowEditButtonInTable}
        isLoading={metricsLoading}
        data={perStoreMetrics}
        isWeek={isWeek}
        date={startDate}
        selectedFilters={selectedFilters}
        onEditFiltersClicked={showFiltersMenu}
      />
    </>
  )
}
